import React, { useEffect, useState } from "react";
import {
  FaTimes,
  FaPaperPlane,
  FaExclamationCircle,
  FaCheckCircle,
  FaTrain,
  FaBed,
} from "react-icons/fa";

const ResponseConvocationModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  convocation,
  mode = "rejected",
}) => {
  const [reason, setReason] = useState("");
  const [needTransport, setNeedTransport] = useState(false);
  const [needAccommodation, setNeedAccommodation] = useState(false);
  const [supportComment, setSupportComment] = useState("");

  const isAcceptedMode = mode === "accepted";
  const event = convocation?.Event || {};

  useEffect(() => {
    if (isOpen) {
      setReason(convocation?.response_reason || "");
      setNeedTransport(convocation?.need_transport_support === true);
      setNeedAccommodation(convocation?.need_accommodation_support === true);
      setSupportComment(convocation?.support_request_comment || "");
    }
  }, [isOpen, convocation]);

  if (!isOpen || !convocation) return null;

  const handleSubmit = () => {
    if (isAcceptedMode) {
      onSubmit({
        response_status: "accepted",
        response_reason: "",
        need_transport_support: needTransport,
        need_accommodation_support: needAccommodation,
        support_request_comment: supportComment.trim(),
      });
      return;
    }

    const cleanedReason = reason.trim();
    if (!cleanedReason) return;

    onSubmit({
      response_status: "rejected",
      response_reason: cleanedReason,
    });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center px-3">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/70 shadow-2xl overflow-hidden">
        <div
          className={`flex items-center justify-between px-5 py-4 border-b border-slate-200/70 dark:border-slate-700/70 text-white ${
            isAcceptedMode
              ? "bg-gradient-to-r from-emerald-600 to-emerald-500"
              : "bg-gradient-to-r from-rose-600 to-rose-500"
          }`}
        >
          <div>
            <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide">
              {isAcceptedMode
                ? "Accepter la convocation"
                : "Refuser la convocation"}
            </h3>
            <p className="text-xs sm:text-sm text-white/90 mt-1">
              {isAcceptedMode
                ? "Confirmez votre disponibilité et précisez si vous souhaitez une prise en charge."
                : "Merci d’indiquer le motif de votre indisponibilité."}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Événement concerné
            </div>
            <div className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
              {event.titre || "Événement"}
            </div>

            <div className="mt-2 text-sm text-slate-600 dark:text-slate-300 space-y-1">
              <div>Poste : {convocation.poste || "Non renseigné"}</div>
              {convocation.tapis ? (
                <div>Tapis : {convocation.tapis}</div>
              ) : null}
            </div>
          </div>

          {isAcceptedMode ? (
            <div className="rounded-xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/20 p-4">
              <div className="text-sm font-semibold text-sky-800 dark:text-sky-300">
                Demande de prise en charge
              </div>
              <p className="mt-1 text-xs sm:text-sm text-sky-700 dark:text-sky-300/90">
                Si vous estimez être concerné (distance &gt; 200 km), vous
                pouvez demander la prise en charge du transport et/ou de
                l’hébergement.
              </p>

              <div className="mt-4 space-y-3">
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={needTransport}
                    onChange={(e) => setNeedTransport(e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium inline-flex items-center gap-2">
                      <FaTrain className="text-sky-600" />
                      Demander la prise en charge du transport
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={needAccommodation}
                    onChange={(e) => setNeedAccommodation(e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium inline-flex items-center gap-2">
                      <FaBed className="text-violet-600" />
                      Demander la prise en charge de l’hébergement
                    </div>
                  </div>
                </label>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100">
                  Commentaire complémentaire
                </label>
                <textarea
                  rows={4}
                  value={supportComment}
                  onChange={(e) => setSupportComment(e.target.value)}
                  placeholder="Précisions utiles : ville de départ, contraintes horaires, besoin de nuit d'hôtel, etc."
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100">
                Motif du refus <span className="text-rose-600">*</span>
              </label>
              <textarea
                rows={6}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Exemple : indisponibilité, déplacement impossible, événement personnel, blessure..."
                className="w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <FaExclamationCircle className="text-rose-500" />
                Ce motif sera visible par les responsables.
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || (!isAcceptedMode && !reason.trim())}
              className={`inline-flex items-center justify-center gap-2 rounded-lg text-white px-4 py-2 text-sm font-semibold disabled:opacity-60 ${
                isAcceptedMode
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-rose-600 hover:bg-rose-700"
              }`}
            >
              {isAcceptedMode ? <FaCheckCircle /> : <FaPaperPlane />}
              {isAcceptedMode ? "Valider mon accord" : "Envoyer le refus"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseConvocationModal;
